import { useEffect, useState } from "react";
import { Users, Trophy, TrendingUp, Wallet, BellRing, AlertTriangle, CheckCircle2, UserCog, IndianRupee } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { api, inr } from "../lib/api";
import { stageOf } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatCard, Card } from "../components/PageHeader";

const COLORS = ["#059669", "#f59e0b", "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6", "#64748b", "#ec4899", "#84cc16"];

const HRDash = ({ d }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
    <StatCard label="Total employees" value={d.total_employees} icon={UserCog} testid="stat-total-employees" />
    <StatCard label="Active" value={d.active} icon={CheckCircle2} tone="blue" testid="stat-active-employees" />
    {Object.entries(d.by_role).map(([r, c]) => <StatCard key={r} label={`${r === "hr" ? "HR" : r} team`} value={c} icon={Users} tone="violet" testid={`stat-role-${r}`} />)}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  const greet = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`${greet}, ${user.name.split(" ")[0]}`} subtitle="Here's what's happening with your business today." />
      {!d ? <div className="text-slate-500">Loading...</div> : d.role === "hr" ? <HRDash d={d} /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Total leads" value={d.total_leads} icon={Users} testid="stat-total-leads" />
            <StatCard label="Active leads" value={d.active_leads} icon={TrendingUp} tone="blue" testid="stat-active-leads" />
            <StatCard label="Won revenue" value={inr(d.revenue)} icon={Trophy} tone="amber" testid="stat-revenue" hint={`${d.won} deals won`} />
            <StatCard label="Conversion rate" value={`${d.conversion_rate}%`} icon={CheckCircle2} tone="violet" testid="stat-conversion" hint="Won / (Won + Lost)" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Follow-ups today" value={d.followups_today} icon={BellRing} tone="amber" testid="stat-followups-today" />
            <StatCard label="Overdue follow-ups" value={d.followups_overdue} icon={AlertTriangle} tone="rose" testid="stat-followups-overdue" />
            <StatCard label="Completed follow-ups" value={d.followups_completed} icon={CheckCircle2} testid="stat-followups-completed" />
            <StatCard label="Outstanding payments" value={inr(d.outstanding)} icon={IndianRupee} tone="rose" testid="stat-outstanding" hint={`Collected ${inr(d.collected)}`} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-5 lg:col-span-2">
              <h3 className="font-semibold mb-4">Leads by stage</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.stages.map((s) => ({ ...s, label: stageOf(s.stage).label }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip /><Bar dataKey="count" fill="#059669" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="font-semibold mb-4">Interested for</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={d.interests} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {d.interests.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-5 lg:col-span-2">
              <h3 className="font-semibold mb-4">Monthly lead trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.monthly}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="leads" fill="#94a3b8" radius={[4, 4, 0, 0]} /><Bar dataKey="won" fill="#059669" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="font-semibold mb-4">{d.leaderboard.length ? "Sales leaderboard" : "Payments"}</h3>
              {d.leaderboard.length ? (
                <div className="space-y-3" data-testid="leaderboard">
                  {d.leaderboard.map((r, i) => (
                    <div key={r.name} className="flex items-center gap-3">
                      <div className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{r.name}</div><div className="text-xs text-slate-500">{r.leads} leads · {r.won} won</div></div>
                      <div className="font-mono text-sm font-semibold">{inr(r.revenue)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Collected</span><span className="font-mono font-semibold text-emerald-700">{inr(d.collected)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Outstanding</span><span className="font-mono font-semibold text-rose-600">{inr(d.outstanding)}</span></div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${d.revenue ? (d.collected * 100) / d.revenue : 0}%` }} /></div>
                  <Wallet className="h-10 w-10 text-slate-200" />
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
